import { Plan, Subscription, ApiResponse, normalizeClubPlanTier, type Region } from '@court-sync/shared';
import { AuthService } from './auth';
import { API_BASE_URL, buildApiUrl } from '../config/api';

export interface CreateSubscriptionData {
  planId: string;
  customerId?: string;
  paymentMethodId?: string;
  region?: Region;
  billingInterval?: 'month' | 'year';
  currency?: string;
  priceId?: string;
  couponCode?: string;
}

export interface SubscriptionResponse {
  subscription: Subscription | null;
  plan: Plan | null;
  clientSecret?: string;
  hasUsedTrial?: boolean;
  hasPaymentMethod?: boolean;
}

class SubscriptionService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await AuthService.getCurrentUserToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  /**
   * Get all available subscription plans
   * @param region Optional region to filter plans by currency
   */
  async getPlans(
    region?: Region,
    productLine?: 'academy' | 'club'
  ): Promise<Plan[]> {
    try {
      const response = await fetch(
        buildApiUrl('/subscription/plans', { region, productLine }),
        {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription plans');
      }

      const data: ApiResponse<Plan[]> = await response.json();
      return data.data || [];
    } catch (error: any) {
      console.error('Error fetching subscription plans:', error);
      throw new Error(error.message || 'Failed to fetch subscription plans');
    }
  }

  /**
   * Club plans from the club product line, with fallback when Stripe metadata is incomplete.
   */
  async getClubPlans(region?: Region): Promise<Plan[]> {
    const clubLinePlans = await this.getPlans(region, 'club');
    if (clubLinePlans.length > 0) {
      return clubLinePlans;
    }
    const allPlans = await this.getPlans(region);
    return allPlans.filter((plan) => normalizeClubPlanTier(plan.internalId ?? null) != null);
  }

  /**
   * Get user's current subscription
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/current`, {
        method: 'GET',
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch current subscription');
      }

      const data: ApiResponse<SubscriptionResponse> = await response.json();
      return data.data || null;
    } catch (error: any) {
      console.error('Error fetching current subscription:', error);
      throw new Error(error.message || 'Failed to fetch current subscription');
    }
  }

  /**
   * Validate subscription promo code (for coach / academy_owner). Returns discount details for UI breakdown.
   */
  async validateCoupon(code: string): Promise<{
    valid: boolean;
    error?: string;
    name?: string;
    percent_off?: number;
    amount_off?: number;
    currency?: string;
  }> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subscription/validate-coupon?code=${encodeURIComponent(code.trim())}`,
        { method: 'GET', headers: await this.getAuthHeaders() }
      );
      const data = await response.json();
      if (!response.ok) {
        return { valid: false, error: data.error || 'Failed to validate' };
      }
      const result = data.data;
      if (!result) return { valid: false, error: 'Invalid response' };
      if (result.valid === false) {
        return { valid: false, error: result.error || 'Invalid promo code' };
      }
      return {
        valid: true,
        name: result.name,
        percent_off: result.percent_off,
        amount_off: result.amount_off,
        currency: result.currency,
      };
    } catch (error: any) {
      return { valid: false, error: error.message || 'Failed to validate promo code' };
    }
  }

  /**
   * @deprecated Use createSubscriptionCheckoutSession — direct create is disabled (Managed Payments).
   */
  async createSubscription(subscriptionData: CreateSubscriptionData): Promise<SubscriptionResponse> {
    const successUrl = `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = window.location.href;
    const checkoutUrl = await this.createSubscriptionCheckoutSession({
      planId: subscriptionData.planId,
      priceId: subscriptionData.priceId,
      region: subscriptionData.region,
      billingInterval: subscriptionData.billingInterval,
      couponCode: subscriptionData.couponCode,
      successUrl,
      cancelUrl,
    });
    window.location.href = checkoutUrl;
    return {} as SubscriptionResponse;
  }

  /**
   * Cancel current subscription
   */
  async cancelSubscription(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      const data: ApiResponse = await response.json();
      return data.data;
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      throw new Error(error.message || 'Failed to cancel subscription');
    }
  }

  /**
   * Upgrade subscription to a different plan
   */
  async upgradeSubscription(planId: string, couponCode?: string): Promise<SubscriptionResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/upgrade`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ planId, couponCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upgrade subscription');
      }

      const data: ApiResponse<SubscriptionResponse> = await response.json();
      return data.data;
    } catch (error: any) {
      console.error('Error upgrading subscription:', error);
      throw new Error(error.message || 'Failed to upgrade subscription');
    }
  }

  /**
   * Get subscription history
   */
  async getSubscriptionHistory(): Promise<Subscription[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/history`, {
        method: 'GET',
        headers: await this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription history');
      }

      const data: ApiResponse<Subscription[]> = await response.json();
      return data.data || [];
    } catch (error: any) {
      console.error('Error fetching subscription history:', error);
      throw new Error(error.message || 'Failed to fetch subscription history');
    }
  }

  /**
   * After Checkout success redirect: poll until Stripe session shows paid / subscription active
   * (webhook may lag). Used by SubscriptionCheckoutSuccess.
   */
  async getCheckoutSessionStatus(sessionId: string): Promise<{ ready: boolean }> {
    const response = await fetch(
      buildApiUrl('/subscription/checkout-session-status', { session_id: sessionId }),
      {
      method: 'GET',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error || 'Failed to verify checkout session'
      );
    }

    const data: ApiResponse<{ ready: boolean }> = await response.json();
    return data.data ?? { ready: false };
  }

  /**
   * Create a Stripe Checkout Session for a new subscription.
   * Returns the Stripe-hosted Checkout URL; the caller should redirect to it.
   */
  async createSubscriptionCheckoutSession(data: {
    priceId?: string;
    planId?: string;
    academyPlanId?: string;
    clubPlanId?: string;
    organizationId?: string;
    region?: Region;
    billingInterval?: 'month' | 'year';
    currency?: string;
    couponCode?: string;
    successUrl: string;
    cancelUrl: string;
    /** Matches Stripe hosted Checkout branding to app light/dark */
    colorScheme?: 'light' | 'dark';
  }): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/create-checkout-session`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const result: ApiResponse<{ url: string }> = await response.json();
      return result.data.url;
    } catch (error: any) {
      console.error('Error creating subscription checkout session:', error);
      throw new Error(error.message || 'Failed to create checkout session');
    }
  }

  /**
   * Add a second product line (academy or club) to an existing org subscription.
   */
  async addProductLine(data: {
    productLine: 'academy' | 'club';
    planId: string;
    billingInterval?: 'month' | 'year';
  }): Promise<{ subscriptionType: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/product-lines`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add product line');
      }

      const result: ApiResponse<{ subscriptionType: string }> = await response.json();
      if (!result.data) {
        throw new Error('Invalid add product line response');
      }
      return result.data;
    } catch (error: any) {
      console.error('Error adding subscription product line:', error);
      throw new Error(error.message || 'Failed to add product line');
    }
  }

  /**
   * Remove academy or club product line from a combined subscription (immediate, prorated).
   */
  async removeProductLine(productLine: 'academy' | 'club'): Promise<{ subscriptionType: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/product-lines`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ productLine }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove product line');
      }

      const result: ApiResponse<{ subscriptionType: string }> = await response.json();
      if (!result.data) {
        throw new Error('Invalid remove product line response');
      }
      return result.data;
    } catch (error: any) {
      console.error('Error removing subscription product line:', error);
      throw new Error(error.message || 'Failed to remove product line');
    }
  }

  /**
   * Create customer portal session for billing management
   */
  async createCustomerPortalSession(returnUrl: string): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/portal`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ returnUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create customer portal session');
      }

      const data: ApiResponse<{ url: string }> = await response.json();
      return data.data.url;
    } catch (error: any) {
      console.error('Error creating customer portal session:', error);
      throw new Error(error.message || 'Failed to create customer portal session');
    }
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
